import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ReferralCard from "@/components/ReferralCard";
import { Plus, PackageCheck, Truck, Clock, Sparkles, Droplet } from "lucide-react";

const STATUS_COLORS = {
  "Order Placed": "bg-amber-100 text-amber-800",
  "Pickup Scheduled": "bg-stone-100 text-stone-800",
  "Picked Up": "bg-blue-100 text-blue-800",
  "In Process": "bg-pink-100 text-pink-800",
  "Out for Delivery": "bg-purple-100 text-purple-800",
  "Delivered": "bg-emerald-100 text-emerald-800",
  "Cancelled": "bg-red-100 text-red-800",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/bookings");
        setBookings(data);
      } finally {
        setBusy(false);
      }
    })();
  }, [user]);

  if (loading || !user) return null;

  const active = bookings.filter((b) => b.status !== "Delivered" && b.status !== "Cancelled");
  const past = bookings.filter((b) => b.status === "Delivered" || b.status === "Cancelled");

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500">Your dashboard</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter text-stone-900 mt-1">
              Hi, {user.name?.split(" ")[0]} 👋
            </h1>
            <p className="text-stone-600 mt-1">Manage your pickups, track deliveries, all in one place.</p>
          </div>
          <Button
            data-testid="new-booking-btn"
            onClick={() => navigate("/book")}
            className="h-12 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold px-6"
          >
            <Plus className="w-4 h-4 mr-1" /> Book a pickup
          </Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { icon: <Clock className="w-5 h-5" />, label: "Active orders", value: active.length },
            { icon: <PackageCheck className="w-5 h-5" />, label: "Completed", value: past.filter(p => p.status === "Delivered").length },
            { icon: <Truck className="w-5 h-5" />, label: "Total bookings", value: bookings.length },
          ].map((s) => (
            <Card key={s.label} className="p-6 rounded-2xl border border-stone-200 bg-white flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">{s.icon}</div>
              <div>
                <div className="text-xs uppercase font-bold tracking-widest text-stone-400">{s.label}</div>
                <div className="font-heading text-2xl font-black text-stone-900">{s.value}</div>
              </div>
            </Card>
          ))}
        </div>

        <ReferralCard />

        <h2 className="font-heading text-2xl font-black text-stone-900 mb-4">Active orders</h2>
        {busy ? (
          <div className="text-stone-500">Loading…</div>
        ) : active.length === 0 ? (
          <Card className="p-10 rounded-2xl border-dashed border-2 border-stone-300 bg-white text-center">
            <Sparkles className="w-8 h-8 text-pink-500 mx-auto mb-2" />
            <p className="font-heading font-bold text-stone-900 text-lg">No active pickups</p>
            <p className="text-stone-600 mt-1">Schedule your first pickup and get fresh laundry in 24h.</p>
            <Button
              data-testid="empty-book-btn"
              onClick={() => navigate("/book")}
              className="mt-5 h-11 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold px-6"
            >
              Book a pickup
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {active.map((b) => (
              <BookingCard key={b.booking_id} b={b} />
            ))}
          </div>
        )}

        {past.length > 0 && (
          <>
            <h2 className="font-heading text-2xl font-black text-stone-900 mt-12 mb-4">Order history</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {past.map((b) => <BookingCard key={b.booking_id} b={b} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BookingCard({ b }) {
  return (
    <Link
      to={`/booking/${b.booking_id}`}
      data-testid={`booking-card-${b.booking_id}`}
      className="block"
    >
      <Card className="p-6 rounded-2xl border border-stone-200 bg-white hover:-translate-y-0.5 hover:shadow-md transition-all">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-bold text-lg text-stone-900">{b.service_tier}</div>
          <Badge className={`${STATUS_COLORS[b.status] || "bg-stone-100 text-stone-800"} rounded-full font-bold`}>{b.status}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-stone-400 font-semibold uppercase text-xs tracking-widest">Pickup</div>
            <div className="text-stone-900 font-semibold">{b.pickup_date}</div>
            <div className="text-stone-500 text-xs">{b.pickup_time_window}</div>
          </div>
          <div>
            <div className="text-stone-400 font-semibold uppercase text-xs tracking-widest">Detergent</div>
            <div className="text-stone-900 font-semibold flex items-center gap-1"><Droplet className="w-3.5 h-3.5 text-pink-500" />{b.detergent}</div>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-stone-100 pt-4">
          <div className="text-xs text-stone-500">#{b.booking_id.slice(-6).toUpperCase()}</div>
          <div className="flex items-center gap-3">
            {b.express && <span className="text-xs font-bold text-pink-600 uppercase tracking-widest">Express</span>}
            <div className="font-heading font-black text-xl text-stone-900">${b.price}</div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
